import { useSelector } from "react-redux"

import { HeatmapDay, useHeatmap } from "~/hooks/heatmap"
import { getSpaces } from "~/store"
import "./HeatmapWidget.scss"
import { Fragment, useEffect, useMemo } from "react"
import consumer from "~/channels/consumer"
import HeatmapRowHours from "./HeatmapRowHours"

const VISIBLE_HOURS: number[] = []
for (let h = 0; h < 24; h++) VISIBLE_HOURS.push(h)

function formatDay(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00")
  return date.toLocaleDateString(undefined, { weekday: "narrow" })
}

function formatHour(hour: number): string {
  return `${String(hour).padStart(2, "0")}`
}

function averageInHour(
  days: HeatmapDay[],
  hour: number,
  type: "work" | "back_to_back" | "break",
  excludeWeekDays = [6, 0], // By default do not include weekends in averages
) {
  const includedDays = days.filter((day) => {
    const dayOfWeek = new Date(day.date).getDay()
    return !excludeWeekDays.includes(dayOfWeek)
  })

  return (
    includedDays.reduce((acc, day) => {
      const dayHour = day.hours.find((h) => h.hour === hour)
      return acc + (dayHour?.[type] ?? 0)
    }, 0) / includedDays.length
  )
}

type Props = {
  user: {
    id: string
    space_name?: string
  }
  weekOffset?: number
}

function HeatmapWidget({ user, weekOffset = 0 }: Props) {
  const { data: days, isLoading, refetch } = useHeatmap(user.id, weekOffset)
  const spaces = useSelector(getSpaces)
  const space = spaces.find(({ name }) => name === user.space_name)
  const currentTimer = space?.current_timer

  useEffect(() => {
    if (!space) return

    const subscription = consumer.subscriptions.create(
      { channel: "SpaceChannel", spaceName: space.name },
      {
        received(data) {
          if (data.type === "usersRegistered") refetch()
        },
      },
    )

    return () => subscription.unsubscribe()
  }, [space?.name])

  const now = new Date()
  const runningTimer =
    currentTimer &&
    !currentTimer.break &&
    new Date(currentTimer.started_at) <= now
      ? currentTimer
      : null

  const averageHours = useMemo(() => {
    if (!days) return []

    return VISIBLE_HOURS.map((hour) => ({
      hour,
      work: averageInHour(days, hour, "work"),
      back_to_back: averageInHour(days, hour, "back_to_back"),
      break: averageInHour(days, hour, "break"),
    }))
  }, [days])

  if (isLoading || !days) return null

  const total = days.reduce((acc, day) => {
    return acc + day.hours.reduce((sum, h) => sum + (h.work ?? 0), 0)
  }, 0)

  return (
    <div className="HeatmapWidget">
      {/* Header row */}
      <div className="HeatmapWidget__day-label" />
      <div className="HeatmapWidget__spacer" />
      {VISIBLE_HOURS.flatMap((hour) => (
        <Fragment key={`header-${hour}`}>
          {(hour === 7 || hour === 17) && (
            <div
              key={`spacer-header-${hour}`}
              className="HeatmapWidget__spacer"
            />
          )}
          <div className="HeatmapWidget__hour-label">{formatHour(hour)}</div>
        </Fragment>
      ))}
      <div className="HeatmapWidget__spacer" />
      <div className="HeatmapWidget__hour-label">T</div>

      {/* Day rows */}
      {days.map((day, i) => (
        <Fragment key={i}>
          <div key={`${day.date}-label`} className="HeatmapWidget__day-label">
            {day.date ? formatDay(day.date) : "Av"}
          </div>

          <div className="HeatmapWidget__spacer" />
          <HeatmapRowHours
            heatmapHours={day.hours}
            date={day.date}
            runningTimer={runningTimer}
          />
          <div className="HeatmapWidget__spacer" />

          <div className="HeatmapWidget__total">
            {new Date(day.date) > now
              ? ""
              : day.hours.reduce((acc, h) => acc + h.work, 0)}
          </div>
        </Fragment>
      ))}

      {/* Averages/Total row */}
      <>
        <div className="HeatmapWidget__day-label" title="Averages">
          A
        </div>

        <div className="HeatmapWidget__spacer" />
        <HeatmapRowHours heatmapHours={averageHours} />
        <div className="HeatmapWidget__spacer" />

        <div className="HeatmapWidget__total" title="Total">
          {total}
        </div>
      </>
    </div>
  )
}

export default HeatmapWidget
